import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, HeartHandshake, Loader2 } from "lucide-react";
import axios from "axios";
import { api, fetchMe, logout } from "@/lib/auth";
import { devLog } from "@/lib/log";

const LOGO_URL = "/logo-transparent.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shown right after sign-up (when the user has no application yet).
 * Asks the user to declare whether they're applying as a vendor or
 * a couple. The chosen role is persisted on the users row + drives
 * which onboarding screen they land on next:
 *   • Vendor → /vendor/apply
 *   • Couple → / (homepage)
 *
 * Admins skip this screen entirely. Users who already have an
 * application also skip it (we route them straight to their dashboard).
 */
const ChooseRole = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [choosing, setChoosing] = useState(null); // "vendor" | "couple" while submitting

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await fetchMe();
      if (cancelled) return;
      if (!me) {
        navigate("/signin", { replace: true });
        return;
      }
      if (me.role === "admin") {
        navigate("/admin", { replace: true });
        return;
      }
      // Skip the picker for users who've already submitted an application.
      try {
        const { data: status } = await api.get("/auth/my-status");
        if (cancelled) return;
        if (status.has_vendor_application) {
          navigate("/vendor/apply", { replace: true });
          return;
        }
        if (status.has_couple_application) {
          navigate("/", { replace: true });
          return;
        }
        if (!status.needs_role_pick && me.role === "vendor") {
          navigate("/vendor/apply", { replace: true });
          return;
        }
        if (!status.needs_role_pick && me.role === "couple") {
          navigate("/couple/signup", { replace: true });
          return;
        }
      } catch (e) {
        devLog("ld_choose_role my-status failed (showing picker):", e?.message);
        // Best-effort — show the picker regardless on /auth/my-status failure.
      }
      if (!cancelled) {
        setUser(me);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const pick = async (role) => {
    if (choosing) return;
    setChoosing(role);
    try {
      // Persist the role server-side so the nav + next-sign-in routing
      // know what kind of user this is even before they submit an app.
      await axios.post(
        `${API}/auth/role`,
        { role },
        { withCredentials: true }
      );
    } catch (e) {
      devLog("ld_choose_role pick API failed (non-fatal):", e?.message);
      // Non-fatal — login will still re-resolve via _resolve_role_for_email
      // once they submit an application. Just continue to the next step.
    }
    if (role === "vendor") navigate("/vendor/apply", { replace: true });
    else navigate("/couple/signup", { replace: true });
  };

  const handleSignOut = async () => {
    await logout();
    navigate("/signin", { replace: true });
  };

  if (loading) {
    return (
      <main
        data-testid="choose-role-loading"
        className="min-h-[100dvh] flex items-center justify-center"
        style={{ backgroundColor: "var(--ld-ivory)" }}
      >
        <Loader2
          size={26}
          strokeWidth={1.4}
          className="animate-spin text-[var(--ld-gold-deep)]"
        />
      </main>
    );
  }

  return (
    <main
      data-testid="choose-role-page"
      className="relative min-h-[100dvh] w-full overflow-x-hidden flex flex-col"
      style={{ backgroundColor: "var(--ld-ivory)" }}
    >
      <header className="relative z-20 flex items-center justify-between gap-6 px-6 md:px-12 pt-5 md:pt-7">
        <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
          Luxé · Directory
        </span>
        <button
          type="button"
          data-testid="choose-role-signout"
          onClick={handleSignOut}
          className="text-[11px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
        >
          Sign out
        </button>
      </header>

      <section className="flex-1 flex flex-col items-center justify-center px-6 md:px-12 py-12 md:py-20">
        <img
          src={LOGO_URL}
          alt="Luxé Directory"
          className="w-[140px] sm:w-[160px] h-auto select-none"
          draggable={false}
        />
        <span className="font-accent text-[11px] tracking-[0.28em] text-[var(--ld-gold-deep)] mt-6">
          Welcome{user?.email ? `, ${user.email}` : ""}
        </span>
        <h1
          data-testid="choose-role-heading"
          className="font-serif-display text-center mt-3 text-3xl sm:text-4xl md:text-[40px] text-[var(--ld-ink)] font-medium leading-tight max-w-2xl"
        >
          Are you a vendor or a couple?
        </h1>
        <p className="mt-4 max-w-md text-center text-[14px] text-[var(--ld-ink-muted)] leading-relaxed">
          Pick the option that fits. You&apos;ll fill out a short onboarding form
          next — once it&apos;s submitted you won&apos;t need to fill it again on
          future sign-ins.
        </p>

        <div className="mt-10 w-full max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-5">
          <button
            type="button"
            data-testid="choose-role-vendor"
            disabled={!!choosing}
            onClick={() => pick("vendor")}
            className="group text-left bg-white border border-[var(--ld-border)] hover:border-[var(--ld-gold-deep)] transition-colors p-7 md:p-8 flex flex-col gap-4 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="inline-flex items-center justify-center w-11 h-11 border border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)]">
              <Sparkles size={18} strokeWidth={1.4} />
            </div>
            <div>
              <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-[var(--ld-gold-deep)]">
                For Professionals
              </span>
              <h2 className="font-serif-display text-2xl md:text-[26px] text-[var(--ld-ink)] mt-2 leading-tight">
                I&apos;m a vendor
              </h2>
              <p className="mt-3 text-[13.5px] text-[var(--ld-ink-muted)] leading-[1.65]">
                Photographers, DJs, planners, venues, attire — share your work
                and reach couples planning their wedding.
              </p>
            </div>
            <span className="mt-auto inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[var(--ld-ink)] group-hover:text-[var(--ld-gold-deep)] transition-colors">
              {choosing === "vendor" ? "Just a moment…" : "Continue to vendor form"}
              {choosing !== "vendor" && (
                <ArrowRight size={13} strokeWidth={1.6} />
              )}
            </span>
          </button>

          <button
            type="button"
            data-testid="choose-role-couple"
            disabled={!!choosing}
            onClick={() => pick("couple")}
            className="group text-left bg-white border border-[var(--ld-border)] hover:border-[var(--ld-gold-deep)] transition-colors p-7 md:p-8 flex flex-col gap-4 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="inline-flex items-center justify-center w-11 h-11 border border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)]">
              <HeartHandshake size={18} strokeWidth={1.4} />
            </div>
            <div>
              <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-[var(--ld-gold-deep)]">
                For Couples
              </span>
              <h2 className="font-serif-display text-2xl md:text-[26px] text-[var(--ld-ink)] mt-2 leading-tight">
                I&apos;m a couple
              </h2>
              <p className="mt-3 text-[13.5px] text-[var(--ld-ink-muted)] leading-[1.65]">
                Discover trusted wedding vendors hand-picked by Luxé. Save your
                favourites and put together your dream day.
              </p>
            </div>
            <span className="mt-auto inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[var(--ld-ink)] group-hover:text-[var(--ld-gold-deep)] transition-colors">
              {choosing === "couple" ? "Just a moment…" : "Continue to couple form"}
              {choosing !== "couple" && (
                <ArrowRight size={13} strokeWidth={1.6} />
              )}
            </span>
          </button>
        </div>
      </section>
    </main>
  );
};

export default ChooseRole;
